import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
  arrayMove
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import './PDFBuilder.css';

const API_URL = process.env.REACT_APP_API_URL;

// ─── Widget Library Definition ─────────────────────────────────────────────
const WIDGET_LIBRARY = {
  spotlight: {
    label: 'Spotlight (Cover Page)',
    description: 'Cross-platform summary stats shown on the cover page',
    targetSection: 'cover',
    widgets: [
      { id: 'spotlight_total_followers', label: 'Total Followers', tooltip: 'Sum of followers across all connected platforms.' },
      { id: 'spotlight_total_video_views', label: 'Total Video Views', tooltip: 'Total video view count across all platforms for the selected time period.' },
      { id: 'spotlight_total_posts', label: 'Total Posts Published', tooltip: 'Total posts published across all connected platforms in the selected period.' }
    ]
  },
  instagram: {
    label: 'Instagram',
    description: 'Metrics from your connected Instagram account',
    targetSection: 'instagram',
    widgets: [
      { id: 'ig_followers', label: 'Followers', tooltip: 'Current Instagram follower count with change from the previous period.' },
      { id: 'ig_video_views', label: 'Video Views', tooltip: 'Total views on video and reel posts in the selected period.' },
      { id: 'ig_engagement_rate', label: 'Engagement Rate', tooltip: 'Average engagement rate (likes + comments per post ÷ followers × 100).' },
      { id: 'ig_avg_engagement', label: 'Avg. Engagement', tooltip: 'Average number of likes + comments per post in the selected period.' },
      { id: 'ig_posts_count', label: 'Posts This Period', tooltip: 'Number of posts published on Instagram during the selected period.' },
      { id: 'ig_content_mix', label: 'Content Mix Bar', tooltip: 'Breakdown of post types (Video, Reel, Carousel, Image) by percentage.' },
      { id: 'ig_growth_chart', label: 'Follower Growth Chart', tooltip: 'Line chart showing follower growth over the selected period.' },
      { id: 'ig_insights', label: 'Key Insights', tooltip: 'Auto-generated insights based on your Instagram performance data.' }
    ]
  },
  facebook: {
    label: 'Facebook',
    description: 'Metrics from your connected Facebook page',
    targetSection: 'facebook',
    widgets: [
      { id: 'fb_followers', label: 'Page Followers', tooltip: 'Current Facebook page follower count with change from the previous period.' },
      { id: 'fb_reach', label: 'Page Reach', tooltip: 'Total unique accounts that saw any of your page content in the last day.' },
      { id: 'fb_impressions', label: 'Impressions', tooltip: 'Total times your page content was displayed (includes repeat views).' },
      { id: 'fb_posts_count', label: 'Posts This Period', tooltip: 'Number of posts published on Facebook during the selected period.' },
      { id: 'fb_engagement_rate', label: 'Engagement Rate', tooltip: 'Average engagement rate (reactions + comments + shares per post ÷ followers × 100).' },
      { id: 'fb_avg_engagement', label: 'Avg. Engagement', tooltip: 'Average total engagements per post in the selected period.' },
      { id: 'fb_link_clicks', label: 'Link Clicks', tooltip: 'Total clicks on links within Facebook posts — shows how many people clicked through to your client\'s website or product pages.' },
      { id: 'fb_total_shares', label: 'Total Shares', tooltip: 'Total number of times posts were shared from your Facebook page.' },
      { id: 'fb_content_mix', label: 'Content Mix Bar', tooltip: 'Breakdown of Facebook post types (Video, Image, Link, Text) by percentage.' },
      { id: 'fb_growth_chart', label: 'Follower Growth Chart', tooltip: 'Line chart showing Facebook follower growth over the selected period.' },
      { id: 'fb_insights', label: 'Key Insights', tooltip: 'Auto-generated insights based on your Facebook performance data.' }
    ]
  },
  fullPage: {
    label: 'Full-Page Sections',
    description: 'Dedicated pages that always take up a full report page',
    widgets: [
      { id: 'ig_top_posts', label: 'Instagram Top Posts', targetSection: 'instagram_top_posts', tooltip: 'A dedicated page showing the top 5 Instagram posts for the period.' },
      { id: 'fb_top_posts', label: 'Facebook Top Posts', targetSection: 'facebook_top_posts', tooltip: 'A dedicated page showing the top 5 Facebook posts for the period.' },
      { id: 'performance_summary', label: 'Performance Summary', targetSection: 'performance_summary', tooltip: 'A dedicated page with AI-generated summary, growth opportunities, and strategy notes.' }
    ]
  }
};

// Minimal section definitions used when a section is missing from a saved template
const SECTION_DEFAULTS = {
  instagram_top_posts: { type: 'full_page', platform: 'instagram', widget: 'ig_top_posts', enabled: false },
  facebook_top_posts:  { type: 'full_page', platform: 'facebook',  widget: 'fb_top_posts',  enabled: false },
  performance_summary: { type: 'full_page', widget: 'performance_summary', enabled: false },
};

const SECTION_META = {
  cover: { icon: '📄', label: 'Cover Page', description: 'Report header, client info, and spotlight stats' },
  instagram: { icon: '📱', label: 'Instagram', description: 'Instagram performance metrics and charts' },
  instagram_top_posts: { icon: '⭐', label: 'Instagram Top Posts', description: 'Top performing Instagram posts (full page)' },
  facebook_top_posts: { icon: '🏆', label: 'Facebook Top Posts', description: 'Top performing Facebook posts (full page)' },
  facebook: { icon: '💬', label: 'Facebook', description: 'Facebook performance metrics and charts' },
  performance_summary: { icon: '📊', label: 'Performance Summary', description: 'AI-generated summary and goals (full page)' }
};

// ─── Preview helpers ────────────────────────────────────────────────────────
const FULLWIDTH_HEIGHT_CLASS = {
  ig_content_mix: 'bar',
  ig_growth_chart: 'chart',
  ig_insights: 'insight',
  fb_content_mix: 'bar',
  fb_growth_chart: 'chart',
  fb_insights: 'insight',
};


const WIDGET_PREVIEW_ICONS = {
  spotlight_total_followers: '👥',
  spotlight_total_video_views: '▶',
  spotlight_total_posts: '📝',
  ig_followers: '👥',
  ig_video_views: '▶',
  ig_engagement_rate: '💬',
  ig_avg_engagement: '❤',
  ig_posts_count: '📝',
  ig_content_mix: '🥧',
  ig_growth_chart: '📈',
  ig_insights: '💡',
  fb_followers: '👥',
  fb_reach: '👁',
  fb_impressions: '📡',
  fb_posts_count: '📝',
  fb_engagement_rate: '💬',
  fb_avg_engagement: '❤',
  fb_link_clicks: '🔗',
  fb_total_shares: '↗',
  fb_content_mix: '🥧',
  fb_growth_chart: '📈',
  fb_insights: '💡',
};

const PLATFORM_BRAND_COLORS = { instagram: '#e1306c', facebook: '#1877f2' };
const PRESET_COLORS = [
  '#ec6433', '#2563eb', '#16a34a', '#9333ea',
  '#dc2626', '#0891b2', '#d97706', '#1f2937',
];

function getTopBarColor(section, accentColor, usePlatformColors) {
  if (usePlatformColors && PLATFORM_BRAND_COLORS[section.platform]) {
    return PLATFORM_BRAND_COLORS[section.platform];
  }
  return accentColor || '#ec6433';
}
function getFullwidthClass(widgetId) {
  return FULLWIDTH_HEIGHT_CLASS[widgetId] || 'bar';
}

// ─── Slot helpers ────────────────────────────────────────────────────────────
function buildPaddedSlots(widgets, widgetRegistry) {
  const rawSlots = (widgets || []).filter(id => id === null || widgetRegistry?.[id]?.type === 'stat');
  const padded = [...rawSlots];
  while (padded.length % 3 !== 0) padded.push(null);
  // Always keep at least one empty slot visible
  if (padded.length === 0 || padded.every(s => s !== null)) {
    padded.push(null, null, null);
  }
  return padded;
}

function trimTrailingNulls(arr) {
  const result = [...arr];
  while (result.length > 0 && result[result.length - 1] === null) result.pop();
  return result;
}

// ─── Fake Preview Data ───────────────────────────────────────────────────────
const FAKE_PREVIEW_DATA = {
  spotlight_total_followers: { value: '12,450', change: '+8.3%', label: 'TOTAL FOLLOWERS' },
  spotlight_total_video_views: { value: '48.2K', change: '+12.1%', label: 'TOTAL VIDEO VIEWS' },
  spotlight_total_posts: { value: '24', change: null, label: 'TOTAL POSTS' },
  ig_followers: { value: '8,920', change: '+5.2%', label: 'FOLLOWERS' },
  ig_video_views: { value: '31.4K', change: '+18.7%', label: 'VIDEO VIEWS' },
  ig_engagement_rate: { value: '3.8%', change: null, label: 'ENGAGEMENT RATE' },
  ig_avg_engagement: { value: '47', change: '+2', label: 'AVG. ENGAGEMENT' },
  ig_posts_count: { value: '16', change: null, label: 'POSTS THIS PERIOD' },
  fb_followers: { value: '3,530', change: '+2.4%', label: 'PAGE FOLLOWERS' },
  fb_reach: { value: '8.2K', change: null, label: 'PAGE REACH' },
  fb_impressions: { value: '22.1K', change: null, label: 'IMPRESSIONS' },
  fb_posts_count: { value: '11', change: null, label: 'POSTS THIS PERIOD' },
  fb_engagement_rate: { value: '2.1%', change: null, label: 'ENGAGEMENT RATE' },
  fb_avg_engagement: { value: '38', change: null, label: 'AVG. ENGAGEMENT' },
  fb_link_clicks: { value: '214', change: null, label: 'LINK CLICKS' },
  fb_total_shares: { value: '47', change: null, label: 'TOTAL SHARES' },
};

// ─── Draggable + Droppable Stat Block ───────────────────────────────────────
// Using useDraggable + useDroppable instead of useSortable so that:
// - The card floats with the cursor during drag
// - No other items shift around (slots stay fixed)
function DraggableDroppableStatBlock({ id, label, onRemove }) {
  const {
    attributes, listeners,
    setNodeRef: setDragRef,
    transform, isDragging
  } = useDraggable({ id });

  const { setNodeRef: setDropRef, isOver } = useDroppable({ id });

  // Combine both refs onto the same element
  const setRef = useCallback((el) => {
    setDragRef(el);
    setDropRef(el);
  }, [setDragRef, setDropRef]);

  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    zIndex: 999,
  } : undefined;

  const icon = WIDGET_PREVIEW_ICONS[id] || '';

  return (
    <div
      ref={setRef}
      style={style}
      className={`preview-stat-block ${isDragging ? 'preview-dragging' : ''} ${isOver && !isDragging ? 'preview-drop-hover' : ''}`}
      {...attributes} {...listeners}
    >
      <div className="preview-stat-content">
        {icon && <span className="preview-widget-icon">{icon}</span>}
        <span className="preview-stat-label">{label}</span>
      </div>
      <button
        className="preview-remove-btn"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => { e.stopPropagation(); onRemove(); }}
        title="Remove widget"
      >×</button>
    </div>
  );
}

// ─── Fixed Droppable Empty Slot ─────────────────────────────────────────────
// Never moves. Only reacts to being hovered by a dragged stat.
function DroppableEmptySlot({ id }) {
  const { setNodeRef, isOver } = useDroppable({ id });

  return (
    <div
      ref={setNodeRef}
      className={`preview-stat-block preview-empty-slot ${isOver ? 'drop-target' : ''}`}
    >
      <span className="preview-empty-slot-hint">
        {isOver ? 'Drop here' : '+ empty'}
      </span>
    </div>
  );
}

// ─── Sortable Full-Width Block (for the stacked list) ───────────────────────
function SortableFullWidthBlock({ id, label, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  const icon = WIDGET_PREVIEW_ICONS[id] || '';
  const heightClass = getFullwidthClass(id);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`preview-fullwidth-block ${heightClass} ${isDragging ? 'preview-dragging' : ''}`}
      {...attributes} {...listeners}
    >
      <div className="preview-fullwidth-content">
        {icon && <span className="preview-widget-icon">{icon}</span>}
        <span className="preview-fullwidth-label">{label}</span>
      </div>
      <button
        className="preview-remove-btn"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={onRemove}
        title="Remove widget"
      >×</button>
    </div>
  );
}

// ─── Section Page Preview ──────────────────────────────────────────────────
function SectionPagePreview({
  section, widgetRegistry, hoveredWidget, accentColor, usePlatformColors,
  isFirst, isLast,
  onRemoveWidget, onReorderWidgets, onToggleSection, onMoveSection
}) {
  const meta = SECTION_META[section.id] || { icon: '📄', label: section.id, description: '' };
  const isFullPage = section.type === 'full_page';
  const isDisabled = section.enabled === false;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const widgets = section.widgets || [];
  const fullWidthIds = widgets.filter(id => id && widgetRegistry?.[id]?.type === 'full_width');
  const paddedSlots = buildPaddedSlots(widgets, widgetRegistry);

  // ── Stat drag end: always swap (slots never shift) ──
  const handleStatDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;

    const activeIdx = paddedSlots.findIndex(s => s === active.id);

    let overIdx;
    const overIsEmpty = String(over.id).startsWith('__slot_');
    if (overIsEmpty) {
      // Empty slot: find by position index encoded in the section-scoped ID
      overIdx = paddedSlots.findIndex((s, i) => s === null && `__slot_${section.id}_${i}` === over.id);
    } else {
      // Another stat: find by widget ID
      overIdx = paddedSlots.findIndex(s => s === over.id);
    }

    if (activeIdx === -1 || overIdx === -1) return;

    const newSlots = [...paddedSlots];
    [newSlots[activeIdx], newSlots[overIdx]] = [newSlots[overIdx], newSlots[activeIdx]];

    const trimmed = trimTrailingNulls(newSlots);
    onReorderWidgets(section.id, [...trimmed, ...fullWidthIds]);
  };

  const handleFullWidthDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIdx = fullWidthIds.indexOf(active.id);
    const newIdx = fullWidthIds.indexOf(over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    const statPart = trimTrailingNulls(paddedSlots);
    onReorderWidgets(section.id, [...statPart, ...arrayMove(fullWidthIds, oldIdx, newIdx)]);
  };

  // Ghost preview for library hover (full-width only — empty slots already show for stats)
  const ghostTargetsThis = hoveredWidget?.targetSection === section.id;
  const ghostAlreadyAdded = ghostTargetsThis && (
    isFullPage
      ? section.widget === hoveredWidget.id && section.enabled
      : widgets.filter(Boolean).includes(hoveredWidget.id)
  );
  const showGhost = ghostTargetsThis && !ghostAlreadyAdded;
  const ghostType = showGhost ? widgetRegistry?.[hoveredWidget.id]?.type : null;

  const topBarColor = getTopBarColor(section, accentColor, usePlatformColors);

  // ── Full-page section card ──
  if (isFullPage) {
    const fullPageLabel = widgetRegistry?.[section.widget]?.label || meta.label;
    return (
      <div className={`section-page-preview ${isDisabled ? 'preview-disabled' : ''}`}>
        <div className="preview-page-top-bar" style={{ background: topBarColor }} />
        <div className="preview-page-header">
          <span className="section-icon">{meta.icon}</span>
          <div>
            <div className="section-name">{meta.label}</div>
            <div className="section-desc">{meta.description}</div>
          </div>
          <div className="preview-header-spacer" />
          <div className="preview-move-buttons">
            <button onClick={() => onMoveSection(section.id, 'up')} disabled={isFirst} title="Move up">↑</button>
            <button onClick={() => onMoveSection(section.id, 'down')} disabled={isLast} title="Move down">↓</button>
          </div>
          <label className="toggle-switch" title={isDisabled ? 'Click to enable' : 'Click to disable'}>
            <input type="checkbox" checked={!isDisabled} onChange={() => onToggleSection(section.id)} />
            <span className="toggle-slider" />
          </label>
        </div>
        <div className="preview-page-content">
          <div className={`preview-full-page-block ${isDisabled ? 'preview-fullpage-off' : ''}`}>
            <span className="preview-fullpage-icon">{meta.icon}</span>
            <span className="preview-fullpage-label">{fullPageLabel}</span>
            <span className="preview-fullpage-hint">This section fills its own page</span>
          </div>
        </div>
      </div>
    );
  }

  // ── Platform / cover section card ──
  const hasStats = paddedSlots.some(s => s !== null);
  const isEmpty = !hasStats && fullWidthIds.length === 0 && !showGhost;
  const isCover = section.type === 'cover';

  return (
    <div className={`section-page-preview ${isDisabled ? 'preview-disabled' : ''}`}>
      <div className="preview-page-top-bar" style={{ background: topBarColor }} />
      <div className="preview-page-header">
        <span className="section-icon">{meta.icon}</span>
        <div>
          <div className="section-name">{meta.label}</div>
          <div className="section-desc">{meta.description}</div>
        </div>
        <div className="preview-header-spacer" />
        <div className="preview-move-buttons">
          <button onClick={() => onMoveSection(section.id, 'up')} disabled={isFirst} title="Move up">↑</button>
          <button onClick={() => onMoveSection(section.id, 'down')} disabled={isLast} title="Move down">↓</button>
        </div>
        {!isCover && (
          <label className="toggle-switch" title={isDisabled ? 'Click to enable' : 'Click to disable'}>
            <input type="checkbox" checked={!isDisabled} onChange={() => onToggleSection(section.id)} />
            <span className="toggle-slider" />
          </label>
        )}
      </div>

      {!isCover && !isDisabled && (
        <div className="preview-platform-info">
          ℹ️ If a client has no {meta.label} data, this section is automatically skipped for that report
        </div>
      )}

      <div className="preview-page-content">
        {/* Stat grid — fixed 3-column, slots stay put during drag */}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleStatDragEnd}
        >
          <div className="preview-stat-grid">
            {paddedSlots.map((id, i) =>
              id !== null ? (
                <DraggableDroppableStatBlock
                  key={id}
                  id={id}
                  label={widgetRegistry?.[id]?.label || id}
                  onRemove={() => onRemoveWidget(section.id, id)}
                />
              ) : (
                <DroppableEmptySlot key={`__slot_${section.id}_${i}`} id={`__slot_${section.id}_${i}`} />
              )
            )}
          </div>
        </DndContext>

        {/* Full-width widget list — stacked, sortable */}
        {(fullWidthIds.length > 0 || (showGhost && ghostType === 'full_width')) && (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleFullWidthDragEnd}>
            <SortableContext items={fullWidthIds} strategy={verticalListSortingStrategy}>
              <div className="preview-fullwidth-list">
                {fullWidthIds.map(id => (
                  <SortableFullWidthBlock
                    key={id}
                    id={id}
                    label={widgetRegistry?.[id]?.label || id}
                    onRemove={() => onRemoveWidget(section.id, id)}
                  />
                ))}
                {showGhost && ghostType === 'full_width' && (
                  <div className={`preview-fullwidth-block ${getFullwidthClass(hoveredWidget.id)} preview-ghost`}>
                    <div className="preview-fullwidth-content">
                      <span className="preview-fullwidth-label">{hoveredWidget.label}</span>
                    </div>
                    <span className="preview-ghost-hint">+ Add</span>
                  </div>
                )}
              </div>
            </SortableContext>
          </DndContext>
        )}

        {isEmpty && (
          <div className="preview-empty-hint">
            Click a widget from the library to add it to this page
          </div>
        )}
      </div>
    </div>
  );
}

// ─── PDF Preview Modal ──────────────────────────────────────────────────────
function PDFPreviewModal({ template, widgetRegistry, orgName, onClose }) {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleKey);
    };
  }, [onClose]);

  const renderStatBox = (widgetId, color) => {
    const data = FAKE_PREVIEW_DATA[widgetId];
    if (!data) return null;
    return (
      <div className="pdf-prev-stat-box" key={widgetId}>
        <div className="pdf-prev-stat-value" style={{ color }}>{data.value}</div>
        {data.change && (
          <div className={`pdf-prev-stat-change ${data.change.startsWith('-') ? 'neg' : 'pos'}`}>
            {data.change}
          </div>
        )}
        <div className="pdf-prev-stat-label">{data.label}</div>
      </div>
    );
  };

  const renderFullWidth = (widgetId, color) => {
    if (widgetId === 'ig_growth_chart' || widgetId === 'fb_growth_chart') {
      return (
        <div className="pdf-prev-chart" key={widgetId}>
          <div className="pdf-prev-widget-label" style={{ color }}>FOLLOWER GROWTH</div>
          <div className="pdf-prev-chart-bars">
            {[38, 45, 42, 55, 51, 63, 59, 71, 67, 80, 75, 88].map((h, i) => (
              <div key={i} className="pdf-prev-chart-bar" style={{ height: `${h}%`, background: color }} />
            ))}
          </div>
        </div>
      );
    }
    if (widgetId === 'ig_content_mix' || widgetId === 'fb_content_mix') {
      return (
        <div className="pdf-prev-content-mix" key={widgetId}>
          <div className="pdf-prev-widget-label" style={{ color: '#666' }}>CONTENT MIX</div>
          <div className="pdf-prev-mix-bar">
            <div style={{ flex: 4, background: color }} />
            <div style={{ flex: 3, background: '#555' }} />
            <div style={{ flex: 2, background: '#999' }} />
            <div style={{ flex: 1, background: '#ccc' }} />
          </div>
          <div className="pdf-prev-mix-legend">
            <span style={{ color }}>Video 40%</span>
            <span style={{ color: '#555' }}>Reel 30%</span>
            <span style={{ color: '#999' }}>Carousel 20%</span>
            <span style={{ color: '#bbb' }}>Image 10%</span>
          </div>
        </div>
      );
    }
    if (widgetId === 'ig_insights' || widgetId === 'fb_insights') {
      return (
        <div className="pdf-prev-insights" key={widgetId}>
          <div className="pdf-prev-widget-label" style={{ color }}>KEY INSIGHTS</div>
          <div className="pdf-prev-insight-line">📈 Follower growth accelerated this period with consistent posting cadence</div>
          <div className="pdf-prev-insight-line">🎥 Video and Reel content is driving 2× more engagement than static images</div>
        </div>
      );
    }
    return null;
  };

  const renderPage = (section) => {
    const meta = SECTION_META[section.id] || { icon: '📄', label: section.id };
    const color = getTopBarColor(section, template.accentColor, template.usePlatformColors);
    const statIds = (section.widgets || []).filter(id => id && widgetRegistry?.[id]?.type === 'stat');
    const fullWidthIds = (section.widgets || []).filter(id => id && widgetRegistry?.[id]?.type === 'full_width');

    if (section.type === 'cover') {
      const sectionLabelMap = {
        instagram: 'Instagram Performance',
        facebook: 'Facebook Performance',
        instagram_top_posts: 'Instagram Top Posts',
        facebook_top_posts: 'Facebook Top Posts',
        performance_summary: 'Performance Summary',
      };
      const contentsSections = template.sections.filter(s => s.id !== 'cover' && s.enabled !== false);

      return (
        <div className="pdf-prev-page" key={section.id}>
          {/* Thin top bar — mirrors doc.rect(0,0,612,8) */}
          <div className="pdf-prev-top-bar" style={{ background: color }} />
          {/* Right-side accent bar */}
          <div className="pdf-prev-cover-accent-bar" style={{ background: color }} />

          <div className="pdf-prev-cover-body">
            {/* Org name + subtitle — mirrors the PDF header block */}
            <div className="pdf-prev-cover-org-name">{(orgName || 'YOUR ORGANIZATION').toUpperCase()}</div>
            <div className="pdf-prev-cover-subtitle">Social Media Analytics Report</div>

            {/* Client info box — mirrors drawBoxWithShadow client block */}
            <div className="pdf-prev-cover-client-box">
              <div className="pdf-prev-cover-client-left">
                <div className="pdf-prev-cover-client-eyebrow">CLIENT</div>
                <div className="pdf-prev-cover-client-name">Acme Coffee Co.</div>
                <div className="pdf-prev-cover-client-period">January 1 – March 31, 2026</div>
              </div>
              <div className="pdf-prev-cover-client-right">
                <div className="pdf-prev-cover-report-date">Report Date: March 12, 2026</div>
                <div className="pdf-prev-cover-comparison">vs. Oct 1 – Dec 31, 2025</div>
              </div>
            </div>

            {/* Spotlight stats */}
            {statIds.length > 0 && (
              <>
                <div className="pdf-prev-cover-heading" style={{ '--accent-color': color }}>At a Glance</div>
                <div className="pdf-prev-stat-row" style={{ marginBottom: 16 }}>
                  {statIds.map(id => renderStatBox(id, color))}
                </div>
              </>
            )}

            {/* Report contents list — dynamically matches what the real PDF builds */}
            <div className="pdf-prev-cover-heading" style={{ '--accent-color': color }}>Report Contents</div>
            <div className="pdf-prev-contents-list">
              {contentsSections.map((s, i) => {
                const label = s.type === 'platform'
                  ? sectionLabelMap[s.platform] || s.id
                  : sectionLabelMap[s.id] || s.id;
                return (
                  <div key={s.id} className="pdf-prev-contents-item">
                    <span className="pdf-prev-contents-num">{i + 2}.</span>
                    <span className="pdf-prev-contents-label">{label}</span>
                    <span className="pdf-prev-contents-dots" />
                    <span className="pdf-prev-contents-page">{i + 2}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="pdf-prev-page-footer">
            <span>Generated by ReportFlow</span>
            <span>Page 1 of {contentsSections.length + 1}</span>
          </div>
        </div>
      );
    }

    if (section.type === 'full_page') {
      if (section.enabled === false) return null;
      return (
        <div className="pdf-prev-page" key={section.id}>
          <div className="pdf-prev-top-bar" style={{ background: color }} />
          <div className="pdf-prev-page-body pdf-prev-fullpage-body">
            <div className="pdf-prev-fullpage-icon">{meta.icon}</div>
            <div className="pdf-prev-fullpage-title">{meta.label}</div>
            <div className="pdf-prev-fullpage-desc">{meta.description}</div>
            {(section.id === 'instagram_top_posts' || section.id === 'facebook_top_posts') && (
              <div className="pdf-prev-top-posts-grid">
                {[1,2,3,4,5].map(i => (
                  <div key={i} className="pdf-prev-top-post-thumb">
                    <div className="pdf-prev-post-img" style={{ background: color + '22' }}>📷</div>
                    <div className="pdf-prev-post-stats">
                      <span>❤ {52 + i * 31}</span>
                      <span>💬 {5 + i * 4}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="pdf-prev-page-footer">
            <span>Generated by ReportFlow</span>
          </div>
        </div>
      );
    }

    // Platform section
    if (section.enabled === false) return null;
    if (statIds.length === 0 && fullWidthIds.length === 0) return null;

    // Preserve null slots so empty spaces render correctly (matches PDF output)
    const paddedStatSlots = (() => {
      const rawSlots = (section.widgets || []).filter(id => id === null || widgetRegistry?.[id]?.type === 'stat');
      const padded = [...rawSlots];
      while (padded.length % 3 !== 0) padded.push(null);
      return padded;
    })();

    return (
      <div className="pdf-prev-page" key={section.id}>
        <div className="pdf-prev-top-bar" style={{ background: color }} />
        <div className="pdf-prev-page-body">
          <div className="pdf-prev-section-label" style={{ borderColor: color, color }}>{meta.label} Performance</div>
          {paddedStatSlots.some(s => s !== null) && (
            <div className="pdf-prev-stat-rows">
              {Array.from({ length: Math.ceil(paddedStatSlots.length / 3) }, (_, row) => (
                <div className="pdf-prev-stat-row" key={row}>
                  {paddedStatSlots.slice(row * 3, row * 3 + 3).map((id, j) =>
                    id
                      ? renderStatBox(id, color)
                      : <div key={`empty-${row}-${j}`} className="pdf-prev-stat-empty" />
                  )}
                </div>
              ))}
            </div>
          )}
          {fullWidthIds.map(id => renderFullWidth(id, color))}
        </div>
        <div className="pdf-prev-page-footer">
          <span>Generated by ReportFlow</span>
        </div>
      </div>
    );
  };

  return (
    <div className="pdf-preview-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="pdf-preview-modal">
        <div className="pdf-preview-modal-header">
          <div>
            <h3>PDF Preview</h3>
            <p>Approximate layout with sample data — pages are actual letter proportions (8.5" × 11")</p>
          </div>
          <button className="pdf-preview-close" onClick={onClose}>✕</button>
        </div>
        <div className="pdf-preview-pages">
          {template.sections.map(section => renderPage(section))}
        </div>
      </div>
    </div>
  );
}

// ─── Main PDFBuilder Component ─────────────────────────────────────────────
function PDFBuilder() {
  const [template, setTemplate] = useState(null);
  const [widgetRegistry, setWidgetRegistry] = useState(null);
  const [orgName, setOrgName] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [hoveredWidget, setHoveredWidget] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const colorPickerRef = useRef(null);

  useEffect(() => { loadTemplate(); }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!colorPickerOpen) return;
    const handleClick = (e) => {
      if (colorPickerRef.current && !colorPickerRef.current.contains(e.target)) {
        setColorPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [colorPickerOpen]);

  const loadTemplate = async () => {
    try {
      setLoading(true);
      const [templateRes, orgRes] = await Promise.all([
        axios.get(`${API_URL}/api/template`, { withCredentials: true }),
        axios.get(`${API_URL}/api/org`, { withCredentials: true })
      ]);
      if (templateRes.data.success) {
        setTemplate(templateRes.data.template);
        setWidgetRegistry(templateRes.data.widgetRegistry);
      }
      if (orgRes.data.success) {
        setOrgName(orgRes.data.org.name || '');
      }
    } catch (err) {
      toast.error('Failed to load template');
    } finally {
      setLoading(false);
    }
  };

  const saveTemplate = async () => {
    try {
      setSaving(true);
      await axios.post(`${API_URL}/api/template`, { template }, { withCredentials: true });
      toast.success('Template saved!');
      setDirty(false);
    } catch (err) {
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const resetTemplate = async () => {
    if (!window.confirm('Reset to the default template? This will remove your customizations.')) return;
    try {
      const res = await axios.delete(`${API_URL}/api/template`, { withCredentials: true });
      if (res.data.success) {
        setTemplate(res.data.template);
        setDirty(false);
        toast.success('Template reset to default');
      }
    } catch (err) {
      toast.error('Failed to reset template');
    }
  };

  const updateTemplate = useCallback((updater) => {
    setTemplate(prev => {
      const next = updater(prev);
      setDirty(true);
      return next;
    });
  }, []);

  const addWidget = (widgetId, sectionId, isFullPage = false) => {
    updateTemplate(prev => {
      let sections = prev.sections;

      // If the section doesn't exist yet (stale saved template), create it from defaults
      if (isFullPage && !sections.some(s => s.id === sectionId)) {
        const def = SECTION_DEFAULTS[sectionId];
        if (def) {
          const newSection = { id: sectionId, ...def };
          // Insert right after the parent platform section if possible
          // (manual last-index search for broad browser compatibility)
          let parentIdx = -1;
          if (def.platform) {
            for (let i = sections.length - 1; i >= 0; i--) {
              if (sections[i].type === 'platform' && sections[i].platform === def.platform) {
                parentIdx = i;
                break;
              }
            }
          }
          sections = parentIdx !== -1
            ? [...sections.slice(0, parentIdx + 1), newSection, ...sections.slice(parentIdx + 1)]
            : [...sections, newSection];
        }
      }

      return {
        ...prev,
        sections: sections.map(s => {
          if (s.id !== sectionId) return s;
          if (isFullPage) return { ...s, enabled: true };
          const current = s.widgets || [];
          if (current.filter(Boolean).includes(widgetId)) return s;

          const widgetType = widgetRegistry?.[widgetId]?.type;
          if (widgetType === 'stat') {
            const fullWidthStart = current.findIndex(id => id && widgetRegistry?.[id]?.type === 'full_width');
            const statEnd = fullWidthStart === -1 ? current.length : fullWidthStart;
            const firstNull = current.slice(0, statEnd).findIndex(id => id === null);
            if (firstNull !== -1) {
              const newWidgets = [...current];
              newWidgets[firstNull] = widgetId;
              return { ...s, widgets: newWidgets };
            }
            const newWidgets = [...current];
            newWidgets.splice(statEnd, 0, widgetId);
            return { ...s, widgets: trimTrailingNulls(newWidgets) };
          }

          // Full-width widgets always append
          return { ...s, widgets: [...current, widgetId] };
        })
      };
    });
  };

  const removeWidget = (sectionId, widgetId) => {
    updateTemplate(prev => ({
      ...prev,
      sections: prev.sections.map(s => {
        if (s.id !== sectionId) return s;
        // Full-page sections use enabled flag instead of widgets array
        if (s.type === 'full_page') return { ...s, enabled: false };
        const newWidgets = s.widgets.map(w => w === widgetId ? null : w);
        return { ...s, widgets: trimTrailingNulls(newWidgets) };
      })
    }));
  };

  const updateAccentColor = (color) => {
    updateTemplate(prev => ({ ...prev, accentColor: color }));
  };

  const reorderWidgets = (sectionId, newWidgets) => {
    updateTemplate(prev => ({
      ...prev,
      sections: prev.sections.map(s =>
        s.id === sectionId ? { ...s, widgets: newWidgets } : s
      )
    }));
  };

  const toggleSection = (sectionId) => {
    updateTemplate(prev => ({
      ...prev,
      sections: prev.sections.map(s => {
        if (s.id !== sectionId) return s;
        // Treat undefined as true (enabled by default) — important for platform sections
        const currentlyEnabled = s.enabled !== false;
        return { ...s, enabled: !currentlyEnabled };
      })
    }));
  };

  const moveSection = (sectionId, direction) => {
    updateTemplate(prev => {
      const idx = prev.sections.findIndex(s => s.id === sectionId);
      if (direction === 'up' && idx === 0) return prev;
      if (direction === 'down' && idx === prev.sections.length - 1) return prev;
      const next = [...prev.sections];
      const swap = direction === 'up' ? idx - 1 : idx + 1;
      [next[idx], next[swap]] = [next[swap], next[idx]];
      return { ...prev, sections: next };
    });
  };

  const isWidgetAdded = (widgetId, targetSection, isFullPageType = false) => {
    if (!template) return false;
    const section = template.sections.find(s => s.id === targetSection);
    if (!section) return false;
    if (isFullPageType) return section.enabled !== false;
    return (section.widgets || []).filter(Boolean).includes(widgetId);
  };

  if (isMobile) return (
    <div className="pdf-builder-mobile-block">
      <div className="pdf-builder-mobile-icon">🖥️</div>
      <h3>Desktop Only</h3>
      <p>The report builder requires a larger screen. Please open it on a desktop or laptop.</p>
    </div>
  );

  if (loading) return <div className="pdf-builder-loading">Loading template...</div>;
  if (!template) return <div className="pdf-builder-loading">Could not load template.</div>;

  return (
    <div className="pdf-builder">

      {/* ── Left panel: Widget Library ── */}
      <div className="pdf-builder-library">
        <div className="library-header">
          <h3>Widget Library</h3>
          <p>Click a widget to add it to your report</p>
        </div>

        {Object.entries(WIDGET_LIBRARY).map(([categoryKey, category]) => (
          <div key={categoryKey} className="library-category">
            <div className="library-category-header">
              <span className="library-category-label">{category.label}</span>
              <span className="library-category-desc">{category.description}</span>
            </div>
            <div className="library-widget-list">
              {category.widgets.map(widget => {
                const targetSection = widget.targetSection || category.targetSection;
                const isFullPageType = categoryKey === 'fullPage';
                const added = isWidgetAdded(widget.id, targetSection, isFullPageType);
                return (
                  <div
                    key={widget.id}
                    className={`library-widget ${added ? 'is-added' : ''}`}
                    onClick={() => added
                      ? removeWidget(targetSection, widget.id)
                      : addWidget(widget.id, targetSection, isFullPageType)
                    }
                    onMouseEnter={() => setHoveredWidget({ id: widget.id, targetSection, label: widget.label })}
                    onMouseLeave={() => setHoveredWidget(null)}
                    title={widget.tooltip}
                  >
                    <div className="library-widget-inner">
                      <span className="library-widget-label">{widget.label}</span>
                      {added
                        ? <span className="library-widget-status added">✓ Added — click to remove</span>
                        : <span className="library-widget-status add">+ Add</span>
                      }
                    </div>
                    <div className="library-widget-tooltip">{widget.tooltip}</div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* ── Right panel: Visual page preview ── */}
      <div className="pdf-builder-canvas">
        <div className="canvas-header">
          <div className="canvas-header-left">
            <h3>Report Layout</h3>
            <p>Pages appear in this order in your PDF — drag within pages to reorder widgets</p>
            {dirty && <span className="unsaved-badge">Unsaved changes</span>}
          </div>
          <div className="canvas-header-actions">
            <div className="accent-color-picker" ref={colorPickerRef}>
              <span className="accent-color-label">Accent</span>
              <button
                className="accent-color-swatch"
                style={{ background: template.accentColor || '#ec6433' }}
                onClick={() => setColorPickerOpen(o => !o)}
                aria-label="Change accent color"
              />
              {colorPickerOpen && (
                <div className="color-picker-popover">
                  <div className="color-picker-presets">
                    {PRESET_COLORS.map(c => (
                      <button
                        key={c}
                        className={`color-preset-swatch ${(template.accentColor || '#ec6433') === c ? 'active' : ''}`}
                        style={{ background: c }}
                        onClick={() => updateAccentColor(c)}
                        aria-label={c}
                      />
                    ))}
                  </div>
                  <div className="color-picker-hex">
                    <span className="hex-prefix">#</span>
                    <input
                      type="text"
                      className="hex-input"
                      defaultValue={(template.accentColor || '#ec6433').replace('#', '')}
                      key={template.accentColor}
                      onChange={e => {
                        const val = e.target.value.replace(/[^0-9a-fA-F]/g, '').slice(0, 6);
                        if (val.length === 6) updateAccentColor('#' + val);
                      }}
                      maxLength={6}
                      placeholder="ec6433"
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="platform-colors-toggle" title="Use Instagram and Facebook brand colors for platform section headers">
              <span className="accent-color-label">Platform colors</span>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={!!template.usePlatformColors}
                  onChange={e => updateTemplate(prev => ({ ...prev, usePlatformColors: e.target.checked }))}
                />
                <span className="toggle-slider" />
              </label>
            </div>
            <button className="btn-preview" onClick={() => setPreviewOpen(true)}>Preview PDF</button>
            <button className="btn-reset" onClick={resetTemplate}>Reset to Default</button>
            <button className="btn-save" onClick={saveTemplate} disabled={saving || !dirty}>
              {saving ? 'Saving...' : 'Save Template'}
            </button>
          </div>
        </div>

        <div className="canvas-sections">
          {template.sections.map((section, idx) => (
            <SectionPagePreview
              key={section.id}
              section={section}
              widgetRegistry={widgetRegistry}
              hoveredWidget={hoveredWidget}
              accentColor={template.accentColor || '#ec6433'}
              usePlatformColors={!!template.usePlatformColors}
              isFirst={idx === 0}
              isLast={idx === template.sections.length - 1}
              onRemoveWidget={removeWidget}
              onReorderWidgets={reorderWidgets}
              onToggleSection={toggleSection}
              onMoveSection={moveSection}
            />
          ))}
        </div>
      </div>

      {previewOpen && (
        <PDFPreviewModal
          template={template}
          widgetRegistry={widgetRegistry}
          orgName={orgName}
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </div>
  );
}

export default PDFBuilder;
